import { useState } from 'react';
import {
  IconChevronDown,
  IconCircleCheck,
  IconCloudUpload,
  IconDevices,
  IconInfoCircle,
  IconLoader2,
  IconAlertCircle,
  IconX,
} from '@tabler/icons-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import PageHeader from '@/components/PageHeader';
import MainCard from '@/components/MainCard';
import { Button } from '@/components/ui/button';
import { buttonVariants } from '@/components/ui/button-variants';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';

interface DistributorOption {
  i_customer: number;
  company_name: string;
}

export function TransactionsPage() {
  const [distributor, setDistributor] = useState<DistributorOption | null>(null);
  const [distributorOptions, setDistributorOptions] = useState<DistributorOption[]>([]);
  const [distributorLoading, setDistributorLoading] = useState(false);
  const [distributorOpen, setDistributorOpen] = useState(false);
  const [distributorSearchInput, setDistributorSearchInput] = useState('');
  const [posFile, setPosFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const searchDistributors = async (query: string) => {
    if (!query || query.length < 2) {
      setDistributorOptions([]);
      return;
    }

    setDistributorLoading(true);
    try {
      const res = await api.get(`/promo-data/distributors-for-transfer?search=${encodeURIComponent(query)}`);
      setDistributorOptions(res.data.data || []);
    } catch (err) {
      console.error('Failed to search distributors', err);
      setDistributorOptions([]);
    } finally {
      setDistributorLoading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!distributor) {
      setError('Please select a distributor.');
      return;
    }

    if (!posFile) {
      setError('Please choose a POS devices file.');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', posFile);
      formData.append('distributorId', String(distributor.i_customer));

      const res = await api.post('/promo-data/load-pos-devices', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSuccess(res.data.message || 'POS devices uploaded successfully.');
      setPosFile(null);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to upload POS devices.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Transactions"
        subtitle="Upload POS device allocations for distributors."
      />

      <MainCard>
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <Alert variant="info">
            <IconInfoCircle className="size-4" />
            <AlertTitle>Instructions</AlertTitle>
            <AlertDescription>
              Choose a distributor, select the POS devices file (CSV or Excel), and upload to process
              allocations.
            </AlertDescription>
          </Alert>

          {error && (
            <Alert variant="destructive">
              <IconAlertCircle className="size-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
              <AlertAction>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  className="size-6"
                  onClick={() => setError('')}
                >
                  <IconX className="size-4" />
                </Button>
              </AlertAction>
            </Alert>
          )}

          {success && (
            <Alert variant="success">
              <IconCircleCheck className="size-4" />
              <AlertTitle>Success</AlertTitle>
              <AlertDescription>{success}</AlertDescription>
              <AlertAction>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  className="size-6"
                  onClick={() => setSuccess('')}
                >
                  <IconX className="size-4" />
                </Button>
              </AlertAction>
            </Alert>
          )}

          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium leading-none">
              Distributor <span className="text-destructive">*</span>
            </span>
            <Popover
              open={distributorOpen}
              onOpenChange={(open) => {
                setDistributorOpen(open);
                if (!open) {
                  setDistributorSearchInput('');
                }
              }}
            >
              <PopoverTrigger
                type="button"
                role="combobox"
                aria-expanded={distributorOpen}
                className={cn(
                  buttonVariants({ variant: 'outline', size: 'default' }),
                  'h-auto min-h-8 w-full justify-between py-2 font-normal'
                )}
              >
                <span className="truncate text-left">
                  {distributor ? distributor.company_name : 'Search distributors (min 2 characters)'}
                </span>
                {distributorLoading ? (
                  <IconLoader2 className="ml-2 size-4 shrink-0 animate-spin opacity-70" />
                ) : (
                  <IconChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
                )}
              </PopoverTrigger>
              <PopoverContent className="w-[min(100vw-2rem,28rem)] p-0" align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Search distributors (min 2 characters)"
                    value={distributorSearchInput}
                    onValueChange={(value) => {
                      setDistributorSearchInput(value);
                      void searchDistributors(value);
                    }}
                  />
                  <CommandList>
                    <CommandEmpty>
                      {distributorLoading
                        ? 'Searching...'
                        : distributorSearchInput.length < 2
                          ? 'Type at least 2 characters to search.'
                          : 'No distributors found.'}
                    </CommandEmpty>
                    <CommandGroup>
                      {distributorOptions.map((option) => (
                        <CommandItem
                          key={option.i_customer}
                          value={`${option.i_customer}-${option.company_name}`}
                          onSelect={() => {
                            setDistributor(option);
                            setDistributorOpen(false);
                          }}
                        >
                          {option.company_name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <input
              id="pos-file-input"
              type="file"
              className="sr-only"
              accept=".csv,.xlsx,.xls"
              onChange={(event) => {
                const file = event.target.files?.[0];
                setPosFile(file ?? null);
              }}
            />
            <label
              htmlFor="pos-file-input"
              className={cn(
                buttonVariants({ variant: 'outline', size: 'default' }),
                'flex w-full cursor-pointer items-center justify-center gap-2 py-4'
              )}
            >
              <IconCloudUpload className="size-4 shrink-0" />
              <span className="truncate">{posFile ? posFile.name : 'Choose POS devices file'}</span>
            </label>
          </div>

          {posFile && (
            <Alert variant="info">
              <IconInfoCircle className="size-4" />
              <AlertTitle>Selected file</AlertTitle>
              <AlertDescription>
                {posFile.name} ({(posFile.size / 1024).toFixed(2)} KB)
              </AlertDescription>
            </Alert>
          )}

          <Button
            type="submit"
            variant="default"
            size="lg"
            disabled={isUploading || !distributor || !posFile}
            className="w-full"
          >
            {isUploading ? (
              <IconLoader2 className="size-4 animate-spin" />
            ) : (
              <IconDevices className="size-4" />
            )}
            {isUploading ? 'Uploading...' : 'Upload POS Devices'}
          </Button>
        </form>
      </MainCard>
    </div>
  );
}
